"use client";
import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faInfoCircle, faEye, faHandsHelping, faNewspaper, faEnvelope, faUsers, faHandshake } from '@fortawesome/free-solid-svg-icons';

const navLinks: Array<{ label: string; href?: string; icon?: any; subLinks?: Array<{ label: string; href: string }>; highlight?: boolean }> = [
  { label: 'Home', href: '/', icon: faHome, highlight: true },
  {
    label: 'About Us',
    icon: faInfoCircle,
    subLinks: [
      { label: 'Our Story', href: '/about' },
      { label: 'Dr. Abdullah – Founder ', href: '/our-founders' },
      { label: 'Vision', href: '/vision' },
      { label: 'Mission', href: '/mission' },
      { label: 'Board of Trustees', href: '/board-of-trustees' },
      { label: 'Management Team', href: '/management-team' },
      { label: 'Partners in Service', href: '/partners-in-service' },
    ],
  },
  {
    label: 'What We Do',
    icon: faEye,
    subLinks: [
      { label: 'Hospital', href: '/hospital' },
      { label: 'Institute', href: '/institute' },
      { label: 'Outreach', href: '/outreach' },
      { label: 'Optical Services', href: '/optical-services' },
      { label: 'Vision Center', href: '/vision-center' },
      { label: 'Eye Bank', href: '/eye-bank' },
    ],
  },
  // {
  //   label: 'Media & Stories',
  //   icon: faNewspaper,
  //   subLinks: [
  //     { label: 'News & Updates', href: '/news' },
  //     { label: 'Patient Stories', href: '/patient-stories' },
  //     { label: 'Photos & Videos', href: '/photos' },
  //     { label: 'Publications', href: '/publications' },
  //   ],
  // },
  {
    label: 'Get Involved',
    icon: faHandsHelping,
    subLinks: [
      { label: 'Donate', href: '/donate' },
      { label: 'Volunteer With Us', href: '/volunteer' },
      { label: 'Organise Outreach', href: '/organise-outreach' },
    ],
  },
  // {
  //   label: 'Support Our Mission',
  //   icon: faHandsHelping,
  //   subLinks: [
  //     { label: 'Donate', href: '/donate' },
  //     { label: 'Sponsor a Patient', href: '/sponsor' },
  //     { label: 'Institutional Partnerships', href: '/institutional-partnerships' },
  //   ],
  // },
  { label: 'Work With Us', href: '/work-with-us' },
  { label: 'Contact Us', href: '/contact', icon: faEnvelope },
];

import { usePathname } from 'next/navigation';

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const pathname = usePathname();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('nav')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMouseEnter = (idx: number) => {
    setActiveDropdown(idx);
  };

  const handleMouseLeave = (event: React.MouseEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !(relatedTarget instanceof HTMLElement) || !relatedTarget.closest('nav')) {
      setActiveDropdown(null);
    }
  };

  // Close menu on navigation (for mobile)
  const handleNavClick = () => setMenuOpen(false);

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50 border-b border-blue-100">
      <div className="container mx-auto flex items-center justify-between py-2 px-4 md:px-6">
        <div className="flex items-center w-full">
          <Link href="/" className="flex items-center gap-0 hover:opacity-80 transition-opacity">
            <Image src="/assets/img/someye-icon.png" alt="Somali Eye Care Icon" width={36} height={36} className="object-contain rounded" priority />
            <span className="font-semibold text-lg whitespace-nowrap hidden md:inline ml-0 mr-8 md:mr-12" style={{ color: 'lab(51 0.2 -59.51)' }}>Somali Eye Care</span>
          </Link>
          <ul className="hidden md:flex items-center space-x-6 text-sm font-semibold">
            {navLinks.map((link, idx) => (
              <li
                key={link.label + idx}
                className="relative group"
                onMouseEnter={() => handleMouseEnter(idx)}
                onMouseLeave={handleMouseLeave}
              >
                <div>
                  <Link
                    href={link.href || '#'}
                    className={`flex items-center gap-2 transition-all whitespace-nowrap ${pathname === link.href ? 'text-gray-800 font-bold' : 'text-gray-700 hover:text-gray-800'}`}
                  >
                    {link.label}
                  </Link>
                </div>
                {link.subLinks && activeDropdown === idx && (
                  <ul
                    className="absolute left-0 top-full mt-2 min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg opacity-100 translate-y-1 transition-all duration-200 pointer-events-auto"
                    onMouseEnter={() => handleMouseEnter(idx)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {link.subLinks.map((sub, subIdx) => (
                      <li key={sub.label + subIdx}>
                        <Link
                          href={sub.href}
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-all"
                        >
                          {sub.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
        <button
          className="md:hidden flex items-center px-2 py-1 border rounded text-blue-700 border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 hover:bg-blue-50 transition-colors"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-lg z-50 border-t border-blue-100 animate-slideDown rounded-b-lg">
          <ul className="flex flex-col gap-2 px-2 py-2 text-sm font-medium">
            {navLinks.map((link, idx) => (
              <li key={link.label + idx} className="relative">
                {link.subLinks ? (
                  <button
                    className="flex items-center w-full gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-all text-left"
                    onClick={() => setActiveDropdown(activeDropdown === idx ? null : idx)}
                    aria-haspopup="true"
                    aria-expanded={activeDropdown === idx}
                  >
                    {link.label}
                    <svg
                      className={`ml-auto w-4 h-4 transition-transform ${activeDropdown === idx ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                ) : (
                  <Link
                    href={link.href || "#"}
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-all"
                  >
                    {link.label}
                  </Link>
                )}
                {link.subLinks && activeDropdown === idx && (
                  <ul className="mt-2 bg-gray-50 border border-gray-200 rounded-lg shadow-lg">
                    {link.subLinks.map((sub, subIdx) => (
                      <li key={sub.label + subIdx}>
                        <Link
                          href={sub.href}
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded-lg transition-all"
                        >
                          {sub.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <style jsx global>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px);}
          to { opacity: 1; transform: translateY(0);}
        }
        .animate-slideDown {
          animation: slideDown 0.2s ease;
        }
      `}</style>
    </nav>
  );
}


